import { format, fromUnixTime, isValid, parse } from 'date-fns';
import { result, stringify } from '../shared';

export const key = 'us_date_mdy';

export const meta = {
  label: 'US Date (MM/DD/YYYY)',
  sensitive: false,
  allowed_contact_types: ['date', 'time'],
  allowed_form_types: ['Date'],
};

const ISO_PATTERN = 'yyyy-MM-dd';
const US_PATTERN = 'MM/dd/yyyy';
// Same cutoff DateField uses so pre-2000 unix values are not mistaken for dates.
const TIMESTAMP_MIN = 946684800;

export function validate(value) {
  const info = inspect(value);
  if (!info) {
    const raw = stringify(value);
    if (!raw) {
      return result(false, '', 'Date is required');
    }
    return result(false, raw, 'Enter a date as MM/DD/YYYY');
  }
  return result(true, info.iso);
}

export function normalize(value) {
  const res = validate(value);
  return res.valid ? res.value : stringify(value);
}

export function display(value, options = {}) {
  const info = inspect(value);
  if (!info) return stringify(value);

  if (options.context === 'edit' || !info.hasTime) {
    return format(info.dateOnly, US_PATTERN);
  }

  return format(info.instant, 'MM/dd/yyyy, hh:mm a');
}

export function getInputProps() {
  return {
    mask: '##/##/####',
    placeholder: 'MM/DD/YYYY',
    inputmode: 'numeric',
  };
}

function inspect(value) {
  const stamped = timestampDate(value);
  if (stamped) {
    const iso = format(stamped, ISO_PATTERN);
    return {
      iso,
      dateOnly: parseStrict(iso, ISO_PATTERN) || stamped,
      instant: stamped,
      hasTime: true,
    };
  }

  const text = stringify(value);
  if (!text) return null;

  const isoMatch = text.match(/^(\d{4}-\d{2}-\d{2})(?:$|T.*)$/);
  if (isoMatch) {
    const dateOnly = parseStrict(isoMatch[1], ISO_PATTERN);
    if (!dateOnly) return null;
    const hasTime = text.charAt(10) === 'T';
    if (!hasTime) {
      return { iso: isoMatch[1], dateOnly, instant: dateOnly, hasTime: false };
    }
    const instant = new Date(text);
    if (!isValid(instant)) {
      return { iso: isoMatch[1], dateOnly, instant: dateOnly, hasTime: false };
    }
    return { iso: isoMatch[1], dateOnly, instant, hasTime: true };
  }

  const slash = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!slash) return null;

  const normalized = `${slash[1].padStart(2, '0')}/${slash[2].padStart(
    2,
    '0'
  )}/${slash[3]}`;
  const dateOnly = parseStrict(normalized, US_PATTERN);
  if (!dateOnly) return null;

  return {
    iso: format(dateOnly, ISO_PATTERN),
    dateOnly,
    instant: dateOnly,
    hasTime: false,
  };
}

function timestampDate(value) {
  if (typeof value !== 'number' && typeof value !== 'string') return null;

  const num = typeof value === 'number' ? value : Number(String(value).trim());
  if (!Number.isFinite(num) || num <= TIMESTAMP_MIN) return null;

  const seconds = num > 9999999999 ? Math.floor(num / 1000) : num;
  const date = fromUnixTime(seconds);
  return isValid(date) ? date : null;
}

function parseStrict(text, pattern) {
  const parsed = parse(text, pattern, new Date(2000, 0, 1));
  if (!isValid(parsed)) return null;
  if (format(parsed, pattern) !== text) return null;
  return parsed;
}
